import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from '../components/rn';
import ActivityIndicator from '../components/LoadingIndicator';
import Ionicons from '@expo/vector-icons/Ionicons';
import { api, NotificationEvent } from '../services/api';
import { fontSize, spacing, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import CompactActionButton from '../components/CompactActionButton';
import { useTranslation } from '../i18n/I18nContext';
import { useUnread } from '../unread/UnreadContext';
import PageHeader from '../components/PageHeader';
import UndoSnackbar from '../components/UndoSnackbar';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import Avatar from '../components/Avatar';
import SystemAvatar from '../components/SystemAvatar';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { reservationNotificationStatusKey } from '../calendar/reservationStatus';

type Props = {
  token: string;
  onBack: () => void;
  onOpen: (event: NotificationEvent) => void;
};

// Batuhan: "7 sn içerisinde geri alma özelliği olmalıdır".
export const MARK_ALL_UNDO_MS = 7000;

type PendingMarkAll = {
  /** Basıldığı anda okunmamış olanlar; geri alınınca yalnız bunlar geri döner. */
  unreadIds: Set<string>;
  unreadCount: number;
  /** Süre içinde açılanlar: sunucuda gerçekten okundu, geri dönmez. */
  opened: Set<string>;
  timer: ReturnType<typeof setTimeout>;
};

function stillUnreadIds(pending: PendingMarkAll): Set<string> {
  return new Set([...pending.unreadIds].filter((id) => !pending.opened.has(id)));
}

function restoreUnread(items: NotificationEvent[], pending: PendingMarkAll): NotificationEvent[] {
  const ids = stillUnreadIds(pending);
  return items.map((event) => (ids.has(event.id) ? { ...event, read_at: null } : event));
}

// Sayaç listeden büyük olabilir (sayfalı liste); açılanlar kadar düşülür.
function restoredUnreadCount(pending: PendingMarkAll): number {
  const openedUnread = [...pending.opened].filter((id) => pending.unreadIds.has(id)).length;
  return Math.max(0, pending.unreadCount - openedUnread);
}

export default function NotificationCenterScreen({ token, onBack, onOpen }: Props) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  // 7.14 · D-550 — başlık içeriğin üstünde yüzüyor; liste aynı yüksekliği
  // tepede boşluk olarak geri veriyor.
  const ustBosluk = screenHeaderContentInset(insets.top);
  const { t, lang } = useTranslation();
  const { refresh: refreshUnread } = useUnread();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [items, setItems] = useState<NotificationEvent[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [failed, setFailed] = useState(false);
  // 7.6 / D-672 — "tümü okundu" hemen sunucuya gitmez: 7 sn boyunca geri
  // alınabilir. Liste okunmuş görünür; süre dolunca, ekrandan çıkılınca ya da
  // liste yenilenince sunucuya yazılır. Geri alınırsa sunucuya hiç gitmez.
  const pendingMarkAll = useRef<PendingMarkAll | null>(null);
  const [undoVisible, setUndoVisible] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Sabit kalsın diye ref: dil yüklenince `t` değişir ve liste yeniden çekilirdi.
  const tokenRef = useRef(token);
  const refreshUnreadRef = useRef(refreshUnread);
  const tRef = useRef(t);
  tokenRef.current = token;
  refreshUnreadRef.current = refreshUnread;
  tRef.current = t;

  const commitMarkAll = useCallback(async () => {
    const pending = pendingMarkAll.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingMarkAll.current = null;
    setUndoVisible(false);
    setCommitting(true);
    try {
      await api.markAllNotificationsRead(tokenRef.current);
      refreshUnreadRef.current();
    } catch {
      setItems((current) => restoreUnread(current, pending));
      setUnreadCount(restoredUnreadCount(pending));
      Alert.alert(tRef.current('common.error'), tRef.current('notifications.centerMarkAllFailed'));
    } finally {
      setCommitting(false);
    }
  }, []);

  // Ekrandan geri alınmadan çıkılırsa işaretleme yine yazılır: niyet duruyor.
  useEffect(() => () => {
    const pending = pendingMarkAll.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingMarkAll.current = null;
    api.markAllNotificationsRead(tokenRef.current)
      .then(() => refreshUnreadRef.current())
      .catch(() => {});
  }, []);

  const load = useCallback(async (isRefresh = false) => {
    // Bekleyen işaretleme önce yazılır; yoksa sunucu okunmamışları geri getirirdi.
    if (pendingMarkAll.current) await commitMarkAll();
    isRefresh ? setRefreshing(true) : setLoading(true);
    setFailed(false);
    try {
      const result = await api.getNotifications(token);
      setItems(result.items);
      setUnreadCount(result.unreadCount);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, commitMarkAll]);

  useEffect(() => { load(); }, [load]);

  async function openItem(item: NotificationEvent) {
    // Geri alma süresinde açılan bildirim gerçekten okundu; geri alınca okunmamışa dönmez.
    pendingMarkAll.current?.opened.add(item.id);
    if (!item.read_at) {
      setItems((current) => current.map((event) =>
        event.id === item.id ? { ...event, read_at: new Date().toISOString() } : event
      ));
      setUnreadCount((count) => Math.max(0, count - 1));
    }
    try {
      await api.markNotificationRead(token, item.id);
    } catch {
      load(true);
    }
    refreshUnread();
    onOpen(item);
  }

  function markAllRead() {
    if (pendingMarkAll.current || committing || unreadCount === 0) return;
    const unreadIds = new Set(items.filter((event) => !event.read_at).map((event) => event.id));
    const now = new Date().toISOString();
    setItems((current) => current.map((event) => ({ ...event, read_at: event.read_at ?? now })));
    setUnreadCount(0);
    pendingMarkAll.current = {
      unreadIds,
      unreadCount,
      opened: new Set(),
      timer: setTimeout(() => { void commitMarkAll(); }, MARK_ALL_UNDO_MS),
    };
    setUndoVisible(true);
  }

  function undoMarkAll() {
    const pending = pendingMarkAll.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingMarkAll.current = null;
    setItems((current) => restoreUnread(current, pending));
    setUnreadCount(restoredUnreadCount(pending));
    setUndoVisible(false);
  }

  function eventTitle(item: NotificationEvent) {
    if (item.type === 'escrow_expired_refunded') {
      return t('notifications.centerEscrowExpiredRefunded');
    }
    // 9.26.3.2.1 — kazanç. Kişi adı YOK: para olaylarında taraf gösterilmiyor
    // (D-127) ve zaten aktör boş geliyor.
    if (item.type === 'escrow_released_earning') {
      return t('notifications.centerEscrowReleasedEarning');
    }
    const name = item.actor_name?.trim() ? item.actor_name : t('notifications.centerUnknownPerson');
    if (item.type === 'call_missed') {
      return `${name} · ${t('call.missed')}`;
    }
    // 9.28 — yaklaşan etkinlik hatırlatması: durum değişikliği değil, zamanı
    // gelen randevunun kendisi. Tür etkinliğin türünü izler.
    if (item.type === 'call_reservation_starting') {
      return t('notifications.centerCallStarting', { name });
    }
    if (item.type === 'meetup_starting') {
      return t('notifications.centerMeetupStarting', { name });
    }
    if (item.type === 'call_reservation_reminder') {
      return t('notifications.centerCallReminder', { name });
    }
    if (item.type === 'meetup_reminder') {
      return t('notifications.centerMeetupReminder', { name });
    }
    if (item.type === 'call_reservation_requested') {
      return t('notifications.centerCallRequest', { name });
    }
    if (item.type === 'meetup_requested') {
      return t('notifications.centerMeetupRequest', { name });
    }
    if (item.type === 'call_reservation_reschedule_requested') {
      return t('notifications.centerCallRescheduleRequest', { name });
    }
    if (item.type === 'meetup_reschedule_requested') {
      return t('notifications.centerMeetupRescheduleRequest', { name });
    }
    if (item.type.includes('_reschedule_')) {
      const rescheduleStatus = t(reservationNotificationStatusKey(item));
      return item.type.startsWith('call_reservation_')
        ? t('notifications.centerCallRescheduleUpdate', { name, status: rescheduleStatus })
        : t('notifications.centerMeetupRescheduleUpdate', { name, status: rescheduleStatus });
    }
    const statusKey = reservationNotificationStatusKey(item);
    const status = t(statusKey);
    return item.type.startsWith('call_reservation_')
      ? t('notifications.centerCallUpdate', { name, status })
      : t('notifications.centerMeetupUpdate', { name, status });
  }

  function eventTime(item: NotificationEvent) {
    const value = item.proposed_at ?? item.created_at;
    const date = new Date(value);
    return `${date.toLocaleDateString(lang, { day: 'numeric', month: 'short', year: 'numeric' })} · ${date.toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}`;
  }

  return (
    <View style={styles.container}>
      <PageHeader
        title={t('notifications.centerTitle')}
        onBack={onBack}
        contentStyle={styles.header}
        floating
        action={
          // 7.6 / D-669 — "Tümünü okundu olarak işaretle" başlığa iki satır
          // küçülerek sığıyordu. Çift tik her dilde aynı yere sığar; tam cümle
          // ekran okuyucuda kalır. Okunmamış yoksa soluk. Batuhan: "Çift tik simgesi".
          <TouchableOpacity
            testID="notification-mark-all"
            style={styles.markAllButton}
            onPress={markAllRead}
            disabled={unreadCount === 0 || committing}
            accessibilityLabel={t('notifications.centerMarkAll')}
            accessibilityState={{ disabled: unreadCount === 0 || committing, busy: committing }}
            accessibilityRole="button">
            <Ionicons
              testID="notification-mark-all-icon"
              name="checkmark-done"
              size={26}
              color={unreadCount === 0 ? colors.gray400 : colors.black}
            />
          </TouchableOpacity>
        }
      />

      {loading ? (
        <View style={styles.center}><ActivityIndicator color={colors.black} /></View>
      ) : failed && items.length === 0 ? (
        <View style={styles.center}>
          <Text style={styles.emptyText}>{t('notifications.centerFailed')}</Text>
          <CompactActionButton
              variant="primary"
              label={t('common.retry')}
              onPress={() => load()}
              style={styles.retryButton}
              testID="notification-initial-retry"
            />
        </View>
      ) : (
        <FlatList
          testID="notification-list"
          data={items}
          keyExtractor={(item) => item.id}
          refreshing={refreshing}
          onRefresh={() => load(true)}
          contentContainerStyle={[
            styles.list,
            { paddingTop: ustBosluk, paddingBottom: spacing.xl + insets.bottom },
            items.length === 0 && styles.emptyList,
          ]}
          scrollIndicatorInsets={{ top: ustBosluk, bottom: insets.bottom }}
          /* Aşağı çekme göstergesi kaydırma çerçevesinin tepesinde doğar, içerik
             dolgusunun değil: kaydırılan alan başlığın ALTINDAN geçtiği için
             gösterge de camın arkasında kalırdı. Kaydırılacak mesafe başlıkla
             aynı yerden türüyor. */
          progressViewOffset={ustBosluk}
          ListHeaderComponent={failed && items.length > 0 ? (
            <View testID="notification-refresh-error" style={styles.refreshError}>
              <Text style={styles.emptyText} accessibilityRole="alert">
                {t('notifications.centerFailed')}
              </Text>
              <TouchableOpacity
                testID="notification-refresh-retry"
                style={styles.retryButton}
                accessibilityRole="button"
                onPress={() => load(true)}>
                <Text style={styles.retryText}>{t('common.retry')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          ListEmptyComponent={<Text style={styles.emptyText}>{t('notifications.centerEmpty')}</Text>}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={styles.row}
              onPress={() => openItem(item)}
              accessibilityRole="button">
              {/* 9.26 / D-646 — kişisi olmayan olay (iade, kazanç): onu sunucu
                  yazdı, rozette nosubject'in logosu. Karar `actor_id`'den, isimden
                  değil: API kişi yokken boş isim gönderiyor ve silinmiş bir hesabın
                  olayı "nosubject yaptı" diye okunmamalı. */}
              {item.actor_id == null ? (
                <SystemAvatar size={46} />
              ) : (
                <Avatar photoUrl={item.actor_photo} name={item.actor_name} size={46} />
              )}
              <View style={styles.rowText}>
                <Text style={[styles.eventTitle, !item.read_at && styles.eventTitleUnread]}>
                  {eventTitle(item)}
                </Text>
                <Text style={styles.eventTime}>{eventTime(item)}</Text>
              </View>
              {!item.read_at && <View style={styles.unreadDot} />}
            </TouchableOpacity>
          )}
        />
      )}

      {undoVisible && (
        <UndoSnackbar
          testID="notification-mark-all-undo"
          text={t('notifications.centerMarkedAll')}
          actionLabel={t('inbox.undo')}
          onAction={undoMarkAll}
          bottom={insets.bottom + spacing.md}
        />
      )}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  markAllButton: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.lg },
  list: { paddingHorizontal: spacing.lg },
  refreshError: { alignItems: 'center', paddingVertical: spacing.md },
  emptyList: { flexGrow: 1, alignItems: 'center', justifyContent: 'center' },
  row: { minHeight: 76, flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  rowText: { flex: 1, minWidth: 0, gap: 4 },
  eventTitle: { color: colors.gray600, fontSize: fontSize.md, lineHeight: 21 },
  eventTitleUnread: { color: colors.black, fontWeight: '700' },
  eventTime: { color: colors.gray400, fontSize: fontSize.sm },
  unreadDot: { width: 9, height: 9, borderRadius: 5, backgroundColor: '#1473E6' },
  separator: { height: StyleSheet.hairlineWidth, backgroundColor: colors.gray200, marginLeft: 62 },
  emptyText: { color: colors.gray400, fontSize: fontSize.md, textAlign: 'center' },
  retryButton: {
    minWidth: 44,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.control,
    backgroundColor: colors.black,
  },
  retryText: { color: colors.white, fontSize: fontSize.md, fontWeight: '600' },
});
